// src/lib/exifRead.js — read when/where a photo was taken, IN THE BROWSER, before we destroy it.
//
// OWNER RULING 2026-09-03: "read it into the DB, strip it from the stored file." The server half
// shipped that day (0848 + core_media.js) and MEASURED ZERO on 18 uploads. The reader was not the
// problem — it was proved working against the live deployed code on 2026-09-04 (a libvips-written
// JPEG gave back 41.715, 44.79 and the timestamp). The problem is upstream and it is OURS:
//
//   ⚠️ `downscalePhoto` re-encodes every photo over 1 MB through a CANVAS, and a canvas has no
//   metadata. A phone photo is 2–12 MB. So every real camera photo an agent uploads arrives at
//   the server already stripped — by us, in their browser, before the ruling's read can happen.
//
// I wrote "images arrive via WhatsApp/Telegram already stripped" into the design doc as the
// explanation. That was a plausible guess written as a conclusion; this is the measured half.
// (It may ALSO be true for some uploads — the two are not exclusive, and neither is provable
// retroactively because we never recorded the original's size.)
//
// So: read the two facts from the ORIGINAL file, hand them to the upload, and let the server
// store them when its own read comes back empty. The stored FILE still carries nothing —
// stripping is unchanged, and this does not weaken it.
//
// ⚠️ PAIRED, NOT SHARED. `readExifBasics` in server-new-libs/lib/core_media.js walks the same
// TIFF structure over sharp's EXIF blob; this one walks it over the raw file, because a browser
// has no sharp and the server has no File. Two inputs, one structure — if you fix a tag offset
// here, check the other. The server's own read WINS when both have an answer (it read the bytes
// it actually stored).
//
// Reads at most the first 256 KB: EXIF lives in APP1, immediately after SOI, and a JPEG that
// hides it past a quarter megabyte is not one we need to support.

const HEAD_BYTES = 262144

/** Walk a TIFF header (the APP1 payload after "Exif\0\0") for GPS + DateTimeOriginal. */
export function readTiff(tiff) {
  try {
    if (!tiff || tiff.length < 16) return null
    const le = String.fromCharCode(tiff[0], tiff[1]) === 'II'
    const dv = new DataView(tiff.buffer, tiff.byteOffset, tiff.byteLength)
    const u16 = i => dv.getUint16(i, le)
    const u32 = i => dv.getUint32(i, le)
    const rat = i => { const n = u32(i), d = u32(i + 4); return d ? n / d : 0 }
    const readIfd = (start, want) => {
      const out = {}
      if (start < 0 || start + 2 > tiff.length) return out
      const n = u16(start)
      for (let k = 0; k < n; k++) {
        const e = start + 2 + k * 12
        if (e + 12 > tiff.length) break
        const tag = u16(e), type = u16(e + 2), count = u32(e + 4)
        if (!want.has(tag)) continue
        const size = ({ 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1, 10: 8 })[type] || 1
        const bytes = size * count
        const at = bytes <= 4 ? e + 8 : u32(e + 8)
        if (at < 0 || at + bytes > tiff.length) continue
        if (type === 2) {
          let sTxt = ''
          for (let j = 0; j < count; j++) { const c = tiff[at + j]; if (!c) break; sTxt += String.fromCharCode(c) }
          out[tag] = sTxt
        } else if (type === 5) {
          out[tag] = Array.from({ length: count }, (_, j) => rat(at + j * 8))
        } else if (type === 3) out[tag] = u16(at)
        else if (type === 4) out[tag] = u32(at)
      }
      return out
    }
    const ifd0 = readIfd(u32(4), new Set([0x8769, 0x8825]))
    const sub = ifd0[0x8769] ? readIfd(ifd0[0x8769], new Set([0x9003])) : {}
    const gps = ifd0[0x8825] ? readIfd(ifd0[0x8825], new Set([1, 2, 3, 4])) : {}
    // Degrees/minutes/seconds → a signed decimal. A hemisphere ref of S or W flips the sign;
    // anything else (including a missing ref) is treated as positive, which is correct for
    // Georgia and wrong nowhere we operate.
    const dms = (a, ref) => (Array.isArray(a) && a.length === 3)
      ? (a[0] + a[1] / 60 + a[2] / 3600) * ((ref === 'S' || ref === 'W') ? -1 : 1) : null
    const lat = dms(gps[2], gps[1]), lng = dms(gps[4], gps[3])
    // EXIF times carry no zone. Every phone here is on Tbilisi time, and an honest local reading
    // beats an instant that is wrong by a zone — stated as +04:00, never guessed as UTC.
    const m = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/.exec(sub[0x9003] || '')
    const takenAt = m ? `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}+04:00` : null
    // 0,0 is the null island — a camera that wrote zeroes knew nothing, so neither do we.
    const geo = (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) ? { lat, lng } : null
    if (!geo && !takenAt) return null
    return { geo, takenAt }
  } catch { return null }
}

/** Find APP1's TIFF payload inside a JPEG's leading bytes. */
export function readJpegExif(bytes) {
  try {
    if (!bytes || bytes.length < 4 || bytes[0] !== 0xFF || bytes[1] !== 0xD8) return null   // not a JPEG
    let i = 2
    while (i + 4 <= bytes.length) {
      if (bytes[i] !== 0xFF) { i++; continue }                 // resync rather than give up
      const marker = bytes[i + 1]
      if (marker === 0xD8 || marker === 0x01 || (marker >= 0xD0 && marker <= 0xD7)) { i += 2; continue }
      if (marker === 0xDA || marker === 0xD9) return null      // image data starts — no EXIF
      const len = (bytes[i + 2] << 8) | bytes[i + 3]
      if (len < 2) return null
      if (marker === 0xE1) {
        const start = i + 4
        const isExif = String.fromCharCode(...bytes.slice(start, start + 4)) === 'Exif'
        if (isExif) return readTiff(bytes.slice(start + 6, i + 2 + len))
      }
      i += 2 + len
    }
    return null
  } catch { return null }
}

/**
 * The whole point, from a File. Never throws and never blocks an upload: a photo whose metadata
 * we cannot read is still a photo, and this returning null must be indistinguishable from a
 * photo that carried nothing.
 * @returns {Promise<{geo:{lat,lng}|null, takenAt:string|null}|null>}
 */
export async function readPhotoMeta(file) {
  try {
    if (!file || typeof file.slice !== 'function') return null
    // iPhone HEIC (2026-10-21): it is converted to a JPEG in the browser (imageDownscale.js heicToJpeg),
    // which drops the metadata — so it is read HERE, from the HEIC, before that happens
    if (/^image\/hei[cf]/i.test(file.type || '') || (!file.type && /\.(heic|heif)$/i.test(file.name || ''))) {
      return readHeicExif(new Uint8Array(await file.slice(0, HEIC_HEAD_BYTES).arrayBuffer()))
    }
    if (!/^image\/(jpeg|jpg)$/i.test(file.type || '')) return null   // PNG/WebP carry no EXIF GPS
    const head = await file.slice(0, HEAD_BYTES).arrayBuffer()
    return readJpegExif(new Uint8Array(head))
  } catch { return null }
}

// A HEIC keeps its EXIF as an item whose payload is a 4-byte offset, then "Exif\0\0", then the same
// TIFF structure a JPEG's APP1 carries. Its place is in the iloc box, but finding the marker followed
// by a TIFF byte-order header is enough and far simpler than walking ISOBMFF boxes; iPhone files keep
// it near the start. Not found in the first megabyte → null, like any photo that carries nothing.
const HEIC_HEAD_BYTES = 1048576
export function readHeicExif(bytes) {
  try {
    for (let i = 0; i + 10 < bytes.length; i++) {
      if (bytes[i] !== 0x45 || bytes[i + 1] !== 0x78 || bytes[i + 2] !== 0x69 || bytes[i + 3] !== 0x66 || bytes[i + 4] || bytes[i + 5]) continue
      const a = bytes[i + 6], b = bytes[i + 7], c = bytes[i + 8], d = bytes[i + 9]
      const ii = a === 0x49 && b === 0x49 && c === 0x2A && d === 0
      const mm = a === 0x4D && b === 0x4D && c === 0 && d === 0x2A
      if (ii || mm) return readTiff(bytes.slice(i + 6))
    }
    return null
  } catch { return null }
}
